import { GridActionsCellItem, GridColDef, GridRowParams, useGridApiRef } from "@mui/x-data-grid-premium";
import { useContext, useEffect, useState } from "react";
import { ModalContext } from "../../../../Components/Modals";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { setupService } from "../../../../Core/Api/services";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Button, Chip, InputAdornment, OutlinedInput, Skeleton, Stack } from "@mui/material";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../Components/Misc/ListPageHeader";
import { YoutubeChannel } from "../../../../Core/Api/Types/youtube-channel";
import ModalCreateOrUpdateYoutubeChannel, {
  ModalCreateOrUpdateYoutubeChannelElementProps,
} from "../../../../Components/Modals/ModalCreateOrUpdateYoutubeChannel/modal-create-or-update-youtube-channel";
import TableCellEnabled from "../../../../Components/Table/TableCellEnabled/table-cell-enabled";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { UserHasAcl } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import TableUuids from "../../../../Components/Table/table-uuids";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import { nanoid } from "@reduxjs/toolkit";

function AdminSettingsYoutubeChannels() {
  const { t } = useTranslation();
  // context
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const apiGrid = useGridApiRef();

  const [youtubeChannels, setYoutubeChannels] = useState<YoutubeChannel[]>([]);
  const [trigger, setTrigger] = useState<boolean>(false);
  const [isLoading, setIsloading] = useState<boolean>(true);

  useEffect(() => {
    setupService.GetYtChannels().then((response) => {
      if (response.success) {
        if (response.ytSettings?.channels) {
          setYoutubeChannels(response.ytSettings?.channels! || []);
        }
        setIsloading(false);
      }
    });
  }, [trigger]);

  const handleEditClick = (channelId: number | null, context: "create" | "update" | "delete") => {
    openModal &&
      (UserHasAcl(AclTypes.aclSuperUser) || UserHasAcl(AclTypes.aclBackofficeYoutubeChannel)) &&
      openModal<ModalCreateOrUpdateYoutubeChannelElementProps>(ModalCreateOrUpdateYoutubeChannel, {
        mode: context,
        channels: youtubeChannels,
        channelId: channelId,
        onConfirm: () => setTrigger((prevState) => !prevState),
      });
  };

  const tableColumns: GridColDef[] = [
    {
      headerName: "Id",
      field: "channelId",
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      renderCell: (data: any) => (data.value ? data.value : "-"),
    },
    {
      headerName: t("common:name"),
      field: "name",
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      renderCell: (data: any) => (data.value ? data.value : "-"),
    },
    {
      headerName: t("common:description"),
      field: "description",
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      renderCell: (data: any) => (data.value ? data.value : "-"),
    },
    {
      headerName: t("field:label.assetLabels"),
      field: "assetLabels",
      flex: CellSize.large,
      minWidth: CellSize.large,
      renderCell: (data: any) => {
        return data.value && data.value.length > 0 ? data.value.map((x) => <Chip label={x} />) : <Chip label={"-"} />;
      },
    },
    {
      headerName: t("common:keywords"),
      field: "keywords",
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: any) => {
        return data.value && data.value.length > 0 ? data.value.map((x) => <Chip label={x} />) : <Chip label={"-"} />;
      },
    },
    {
      headerName: t("common:playlistId"),
      field: "playlistId",
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: any) => {
        return data.value && data.value.length > 0 ? data.value.map((x) => <Chip label={x} />) : <Chip label={"-"} />;
      },
    },
    {
      headerName: t("common:ownership"),
      field: "ownership",
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: any) => {
        return data.value && data.value.length > 0 ? data.value.map((x) => <Chip label={x} />) : <Chip label={"-"} />;
      },
    },
    {
      headerName: t("field:label.usagePolicy"),
      field: "usagePolicy",
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: any) => {
        return <Chip label={data.value} color="default" />;
      },
    },
    {
      headerName: t("common:adBreakTime"),
      field: "adBreakTime",
      flex: CellSize.small,
      minWidth: CellSize.small,
      renderCell: (data: any) => {
        return <Chip label={data.value} color="default" />;
      },
    },
    {
      headerName: t("common:blockOutsideOwnership"),
      field: "blockOutsideOwnership",
      flex: CellSize.small,
      minWidth: CellSize.small,
      renderCell: (data: any) => {
        return <TableCellEnabled value={data.value} />;
      },
    },
    {
      headerName: t("common:preRoll"),
      field: "preRoll",
      flex: CellSize.small,
      minWidth: CellSize.small,
      renderCell: (data: any) => {
        return <TableCellEnabled value={data.value} />;
      },
    },
    {
      headerName: "End roll",
      field: "endRoll",
      flex: CellSize.small,
      minWidth: CellSize.small,
      renderCell: (data: any) => {
        return <TableCellEnabled value={data.value} />;
      },
    },
    {
      headerName: t("common:isMadeForKids"),
      field: "isMadeForKids",
      flex: CellSize.small,
      minWidth: CellSize.small,
      renderCell: (data: any) => {
        return <TableCellEnabled value={data.value} />;
      },
    },
    {
      headerName: t("common:notifySubscribers"),
      field: "notifySubscribers",
      flex: CellSize.small,
      minWidth: CellSize.small,
      renderCell: (data: any) => {
        return <TableCellEnabled value={data.value} />;
      },
    },
    {
      headerName: "",
      field: "actions",
      type: "actions",
      align: "right",
      flex: CellSize.actionsSmall,
      minWidth: CellSize.actionsSmall,
      getActions: (params: GridRowParams) => {
        if (!params || !params.row) return [];
        const actions = [
          <GridActionsCellItem
            sx={{ mr: 1 }}
            icon={<EditOutlinedIcon />}
            label={t("common:editUpperCase")}
            onClick={() => handleEditClick(params.row["channelId"], "update")}
          />,
          <GridActionsCellItem
            icon={<DeleteOutlineOutlinedIcon />}
            label={t("common:delete")}
            color="error"
            onClick={() => handleEditClick(params.row["channelId"], "delete")}
          />,
        ];
        return actions;
      },
    },
  ];

  const additionalActions = (
    <>
      {UserHasAcl(AclTypes.aclSuperUser) ||
        (UserHasAcl(AclTypes.aclBackofficeYoutubeChannel) && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleEditClick(null, "create")}
          >
            {t("button:createChannel")}
          </Button>
        ))}
    </>
  );

  return (
    <DataTable
      loading={isLoading}
      tableKey={TableUuids.SETTINGS_YOUTUBE_CHANNELS}
      additionalActions={additionalActions}
      apiRef={apiGrid}
      columns={tableColumns}
      rows={youtubeChannels}
      getRowHeight={() => "auto"}
      getRowId={(row) => row.channelId}
      pagination={true}
      disableRowSelectionOnClick
      initialState={{
        pinnedColumns: { left: ["channelId", "name"], right: ["actions"] },
      }}
    />
  );
}

export default AdminSettingsYoutubeChannels;
